/* eslint-disable react/jsx-no-bind */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import { BatchReportDetails } from './batchReportDetails.component';
import Config from '../../config';
import JimJobInfo from '../../shared/jim/jimJobInfo.component';
import Spinner from '../../shared/spinner.component';
import Select from '../../shared/reactSelect/select.component';
import { withPermissions, permissions } from '../../shared/authorization';
import { useCustomGroups } from '../../shared/queries/customGroup';
import { CUSTOM_GROUP_TYPES } from '../../administration/customGroupsPage/constants';
import { userRoleSelector } from '../../shared/store/selectors';
import UserRoles from '../../shared/authorization/userRoles';

import { useBatchExportJob } from './useBatchExportJob.hook';
import { CustomGroup } from '@client-shared/types/customGroup';
import { CustomReportOption } from './types';

type BatchReportsPageProps = {
  canModify: boolean,
  canView: boolean,
  canModifyInSelectedPeriod: boolean,
};

const formatBatchReportsOptions = (customGroups: CustomGroup[], userRole: string | null) => customGroups
  .filter(customGroup => customGroup.groupType === CUSTOM_GROUP_TYPES.BATCH_REPORT)
  .filter(customGroup => {
    if (customGroup.archivalFlag || customGroup.sftpDeliveryFlag) {
      return userRole === UserRoles.POWER_USER || userRole === UserRoles.SUPER_USER;
    }
    return true;
  })
  .map(batchReportCustomGroup => ({
    label: batchReportCustomGroup.name,
    value: batchReportCustomGroup.id,
  }));

const BatchReportsPage: React.FC<BatchReportsPageProps> = () => {
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [selectedCustomGroup, setSelectedCustomGroup] = useState<CustomReportOption | null>(null);
  const { data: customGroups, isFetching: isFetchingCustomGroups } = useCustomGroups();
  const userRole = useSelector(userRoleSelector);

  const onBatchReportGroupChange = (selectedCustomGroup: CustomReportOption) => {
    setSelectedCustomGroup(selectedCustomGroup);
    setIsEditEnabled(false);
  };

  const {
    onAddJobError,
    onAddJobSuccess,
    onExportStart,
    onJobComplete,
    isExportInProgress,
    jobId,
  } = useBatchExportJob();

  const handlOnJobError = (error: Error) => {
    toastr.error('', `Unable to start batch export: ${error.message}`);
    onAddJobError();
  };

  const updateIsEditEnabled = () => setIsEditEnabled(!isEditEnabled);
  const jobConfig = useMemo(() => {
    if (!selectedCustomGroup) {
      return Config.jimJobs.batchReportsExport;
    }
    const selectedGroup = customGroups.find(group => group.id === selectedCustomGroup.value);
    if (selectedGroup?.archivalFlag) {
      return Config.jimJobs.batchArchiveReportsExport;
    }
    return Config.jimJobs.batchReportsExport;
  }, [customGroups, selectedCustomGroup]);

  return (
    <div className="client-administration-page page">
      <div className="container-fluid">
        <div className="row no-gutters page-content-container">
          <div className="col">
            <div className="d-flex flex-row justify-content-between">
              <h5>Batch Reports</h5>
            </div>
          </div>
        </div>
        <div className="row no-gutters page-content-container">
          <div className="col">
            <Select
              className="select-custom-reports"
              clearable={false}
              disabled={isFetchingCustomGroups || customGroups.length === 0}
              options={formatBatchReportsOptions(customGroups || [], userRole)}
              value={selectedCustomGroup}
              placeholder="Batch Report Group"
              onChange={onBatchReportGroupChange}
              isLoading={isFetchingCustomGroups}
              virtualized
              closeOnSelect
            />
            {isFetchingCustomGroups && <Spinner />}
          </div>
        </div>
        <div className="row no-gutters page-content-container">
          <div className="col">
            <JimJobInfo
              jobConfig={jobConfig}
              onJobComplete={onJobComplete}
              showProgressBar
              forceSelectionById
              jobId={jobId}
              autoMarkNotificationAsSeen={false}
            />
          </div>
        </div>
        <div className="no-gutters page-content-container">
          {
            selectedCustomGroup && <BatchReportDetails
              selectedBatchReport={selectedCustomGroup}
              onAddJobSuccess={onAddJobSuccess}
              onAddJobError={handlOnJobError}
              isExportInProgress={isExportInProgress}
              onExportStart={onExportStart}
              isEditEnabled={isEditEnabled}
              setIsEditEnabled={updateIsEditEnabled}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default withPermissions(permissions.CUSTOM_REPORTS)(BatchReportsPage);
